// Dependency-free QR Code (ISO/IEC 18004) encoder — byte mode only, versions
// 1–10, error-correction levels L/M/Q/H (default 'M'). Byte mode is the only
// mode implemented on purpose: the "add friend" URL contains lowercase letters
// and ':' '/' '#' which are outside QR's alphanumeric alphabet, so alphanumeric
// mode would corrupt them. This module is pure data-in/data-out (no DOM) so it
// unit-tests cleanly and feeds the SVG renderer in QrCode.tsx.
//
// Correctness-critical pieces implemented per spec: GF(256) Reed–Solomon EC,
// block splitting + interleaving, all 8 data masks scored with the four
// standard penalty rules, and BCH-protected format (and version ≥7 version)
// information. Output is a scannable symbol matrix; the caller adds the quiet
// zone.

export type EcLevel = 'L' | 'M' | 'Q' | 'H';

/** Largest version this encoder supports. */
export const MAX_VERSION = 10;

// --- Error-correction block table (ISO/IEC 18004, versions 1–10) -------------
// Per (ecLevel, version): [ecCodewordsPerBlock, groups] where each group is
// [blockCount, dataCodewordsPerBlock]. Totals were cross-checked against the
// standard's data-capacity table.
type Group = readonly [blocks: number, dataPerBlock: number];
type EcEntry = readonly [ecPerBlock: number, groups: readonly Group[]];

const EC_TABLE: Record<EcLevel, readonly EcEntry[]> = {
  L: [
    [7, [[1, 19]]],
    [10, [[1, 34]]],
    [15, [[1, 55]]],
    [20, [[1, 80]]],
    [26, [[1, 108]]],
    [18, [[2, 68]]],
    [20, [[2, 78]]],
    [24, [[2, 97]]],
    [30, [[2, 116]]],
    [18, [[2, 68], [2, 69]]],
  ],
  M: [
    [10, [[1, 16]]],
    [16, [[1, 28]]],
    [26, [[1, 44]]],
    [18, [[2, 32]]],
    [24, [[2, 43]]],
    [16, [[4, 27]]],
    [18, [[4, 31]]],
    [22, [[2, 38], [2, 39]]],
    [22, [[3, 36], [2, 37]]],
    [26, [[4, 43], [1, 44]]],
  ],
  Q: [
    [13, [[1, 13]]],
    [22, [[1, 22]]],
    [18, [[2, 17]]],
    [26, [[2, 24]]],
    [18, [[2, 15], [2, 16]]],
    [24, [[4, 19]]],
    [18, [[2, 14], [4, 15]]],
    [22, [[4, 18], [2, 19]]],
    [20, [[4, 16], [4, 17]]],
    [24, [[6, 19], [2, 20]]],
  ],
  H: [
    [17, [[1, 9]]],
    [28, [[1, 16]]],
    [22, [[2, 13]]],
    [16, [[4, 9]]],
    [22, [[2, 11], [2, 12]]],
    [28, [[4, 15]]],
    [26, [[4, 13], [1, 14]]],
    [26, [[4, 14], [2, 15]]],
    [24, [[4, 12], [4, 13]]],
    [28, [[6, 15], [2, 16]]],
  ],
};

// Alignment-pattern centre coordinates per version (index = version-1).
const ALIGN_POSITIONS: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

// 2-bit EC-level indicator used inside the format information.
const EC_FORMAT_BITS: Record<EcLevel, number> = { L: 1, M: 0, Q: 3, H: 2 };

// --- GF(256) arithmetic for Reed–Solomon ------------------------------------
// Primitive polynomial x^8 + x^4 + x^3 + x^2 + 1 (0x11D).
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]!;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[GF_LOG[a]! + GF_LOG[b]!]!;
}

/** Reed–Solomon generator polynomial of the given degree (length = degree). */
function rsDivisor(degree: number): number[] {
  const result: number[] = new Array(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < result.length; j++) {
      result[j] = gfMul(result[j]!, root);
      if (j + 1 < result.length) result[j] = result[j]! ^ result[j + 1]!;
    }
    root = gfMul(root, 0x02);
  }
  return result;
}

/** EC codewords for `data` under the divisor produced by rsDivisor(). */
function rsRemainder(data: readonly number[], divisor: readonly number[]): number[] {
  const result: number[] = new Array(divisor.length).fill(0);
  for (const b of data) {
    const factor = b ^ result[0]!;
    result.shift();
    result.push(0);
    for (let i = 0; i < result.length; i++) result[i] = result[i]! ^ gfMul(divisor[i]!, factor);
  }
  return result;
}

// --- Bit / capacity helpers -------------------------------------------------
function charCountBits(version: number): number {
  // Byte mode: 8 bits for versions 1–9, 16 bits for versions 10–26.
  return version < 10 ? 8 : 16;
}

function totalDataCodewords(version: number, ec: EcLevel): number {
  const [, groups] = EC_TABLE[ec][version - 1]!;
  let n = 0;
  for (const [blocks, dataPerBlock] of groups) n += blocks * dataPerBlock;
  return n;
}

function selectVersion(byteLength: number, ec: EcLevel): number {
  for (let version = 1; version <= MAX_VERSION; version++) {
    const capacityBits = totalDataCodewords(version, ec) * 8;
    const requiredBits = 4 + charCountBits(version) + byteLength * 8;
    if (requiredBits <= capacityBits) return version;
  }
  throw new Error(
    `QR: input of ${byteLength} bytes exceeds capacity of version ${MAX_VERSION} at EC level ${ec}`,
  );
}

// --- Data codeword construction ---------------------------------------------
function buildDataCodewords(data: Uint8Array, version: number, ec: EcLevel): number[] {
  const totalData = totalDataCodewords(version, ec);
  const capacityBits = totalData * 8;
  const bits: number[] = [];
  const pushBits = (value: number, length: number): void => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >> i) & 1);
  };

  pushBits(0b0100, 4); // byte-mode indicator
  pushBits(data.length, charCountBits(version));
  for (const b of data) pushBits(b, 8);

  // Terminator: up to four zero bits, never past capacity.
  for (let i = 0; i < 4 && bits.length < capacityBits; i++) bits.push(0);
  // Pad to a byte boundary.
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j]!;
    codewords.push(byte);
  }
  // Pad bytes: 0xEC / 0x11 alternating.
  const PAD = [0xec, 0x11];
  for (let i = 0; codewords.length < totalData; i++) codewords.push(PAD[i % 2]!);
  return codewords;
}

/** Split data into blocks, append EC per block, then interleave both. */
function buildFinalCodewords(dataCodewords: number[], version: number, ec: EcLevel): number[] {
  const [ecPerBlock, groups] = EC_TABLE[ec][version - 1]!;
  const divisor = rsDivisor(ecPerBlock);

  const dataBlocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;
  for (const [blocks, dataPerBlock] of groups) {
    for (let b = 0; b < blocks; b++) {
      const block = dataCodewords.slice(offset, offset + dataPerBlock);
      offset += dataPerBlock;
      dataBlocks.push(block);
      ecBlocks.push(rsRemainder(block, divisor));
    }
  }

  const result: number[] = [];
  const maxData = Math.max(...dataBlocks.map((b) => b.length));
  for (let i = 0; i < maxData; i++) {
    for (const block of dataBlocks) if (i < block.length) result.push(block[i]!);
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ecBlocks) result.push(block[i]!);
  }
  return result;
}

// --- Matrix construction ----------------------------------------------------
interface Grid {
  size: number;
  cells: number[][]; // 0 = light, 1 = dark
  reserved: boolean[][]; // true = function module (never masked, skipped by data)
}

function newGrid(size: number): Grid {
  return {
    size,
    cells: Array.from({ length: size }, () => new Array<number>(size).fill(0)),
    reserved: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
  };
}

function setFn(grid: Grid, row: number, col: number, dark: boolean): void {
  grid.cells[row]![col] = dark ? 1 : 0;
  grid.reserved[row]![col] = true;
}

function drawFinder(grid: Grid, cr: number, cc: number): void {
  for (let dr = -4; dr <= 4; dr++) {
    for (let dc = -4; dc <= 4; dc++) {
      const r = cr + dr;
      const c = cc + dc;
      if (r < 0 || r >= grid.size || c < 0 || c >= grid.size) continue;
      const dist = Math.max(Math.abs(dr), Math.abs(dc));
      setFn(grid, r, c, dist !== 2 && dist !== 4);
    }
  }
}

function drawAlignment(grid: Grid, cr: number, cc: number): void {
  for (let dr = -2; dr <= 2; dr++) {
    for (let dc = -2; dc <= 2; dc++) {
      setFn(grid, cr + dr, cc + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
    }
  }
}

/** Positions of every format-information module (both copies), with its bit. */
function formatPositions(size: number): { row: number; col: number; bit: number }[] {
  const out: { row: number; col: number; bit: number }[] = [];
  // Copy 1: around the top-left finder.
  for (let i = 0; i <= 5; i++) out.push({ row: i, col: 8, bit: i });
  out.push({ row: 7, col: 8, bit: 6 });
  out.push({ row: 8, col: 8, bit: 7 });
  out.push({ row: 8, col: 7, bit: 8 });
  for (let i = 9; i < 15; i++) out.push({ row: 8, col: 14 - i, bit: i });
  // Copy 2: split under top-right and beside bottom-left finders.
  for (let i = 0; i < 8; i++) out.push({ row: 8, col: size - 1 - i, bit: i });
  for (let i = 8; i < 15; i++) out.push({ row: size - 15 + i, col: 8, bit: i });
  return out;
}

function formatBits(ec: EcLevel, mask: number): number {
  const data = (EC_FORMAT_BITS[ec] << 3) | mask; // 5 bits
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ (((rem >> 9) & 1) * 0x537);
  return (((data << 10) | rem) ^ 0x5412) & 0x7fff; // 15 bits, BCH(15,5) masked
}

function versionBits(version: number): number {
  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ (((rem >> 11) & 1) * 0x1f25);
  return ((version << 12) | rem) & 0x3ffff; // 18 bits, BCH(18,6)
}

function buildFunctionPatterns(version: number): Grid {
  const size = version * 4 + 17;
  const grid = newGrid(size);

  // Timing patterns first; finders overwrite their overlap (matches spec order).
  for (let i = 0; i < size; i++) {
    setFn(grid, 6, i, i % 2 === 0);
    setFn(grid, i, 6, i % 2 === 0);
  }

  drawFinder(grid, 3, 3);
  drawFinder(grid, 3, size - 4);
  drawFinder(grid, size - 4, 3);

  const centres = ALIGN_POSITIONS[version - 1] ?? [];
  const n = centres.length;
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      // Skip the three that collide with finder patterns.
      if ((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0)) continue;
      drawAlignment(grid, centres[i]!, centres[j]!);
    }
  }

  // Fixed dark module.
  setFn(grid, size - 8, 8, true);

  // Reserve format-information modules (values written per mask later).
  for (const { row, col } of formatPositions(size)) grid.reserved[row]![col] = true;

  // Version information (versions ≥ 7): function modules, mask-independent.
  if (version >= 7) {
    const bits = versionBits(version);
    for (let i = 0; i < 18; i++) {
      const bit = ((bits >> i) & 1) === 1;
      const a = size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      setFn(grid, b, a, bit);
      setFn(grid, a, b, bit);
    }
  }

  return grid;
}

/** Zig-zag placement of the codeword bitstream into the data region. */
function placeData(grid: Grid, codewords: readonly number[]): void {
  const { size } = grid;
  let bitIndex = 0;
  const totalBits = codewords.length * 8;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // skip the vertical timing column
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const col = right - j;
        const upward = ((right + 1) & 2) === 0;
        const row = upward ? size - 1 - vert : vert;
        if (grid.reserved[row]![col]) continue;
        if (bitIndex < totalBits) {
          const bit = (codewords[bitIndex >> 3]! >> (7 - (bitIndex & 7))) & 1;
          grid.cells[row]![col] = bit;
        }
        // Remainder bits (beyond the codewords) stay 0.
        bitIndex++;
      }
    }
  }
}

function maskCondition(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0:
      return (row + col) % 2 === 0;
    case 1:
      return row % 2 === 0;
    case 2:
      return col % 3 === 0;
    case 3:
      return (row + col) % 3 === 0;
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    case 7:
      return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0;
    default:
      throw new Error(`QR: invalid mask ${mask}`);
  }
}

function applyMask(cells: number[][], reserved: boolean[][], mask: number): void {
  const size = cells.length;
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (!reserved[row]![col] && maskCondition(mask, row, col)) {
        cells[row]![col] = cells[row]![col]! ^ 1;
      }
    }
  }
}

function drawFormat(cells: number[][], size: number, ec: EcLevel, mask: number): void {
  const bits = formatBits(ec, mask);
  for (const { row, col, bit } of formatPositions(size)) {
    cells[row]![col] = (bits >> bit) & 1;
  }
}

// --- Mask penalty scoring (four standard rules) -----------------------------
function penaltyScore(cells: readonly number[][]): number {
  const size = cells.length;
  let penalty = 0;

  // Rule 1: runs of ≥5 same-coloured modules in each row and column.
  const runPenalty = (get: (i: number) => number): number => {
    let sub = 0;
    let runColor = -1;
    let runLen = 0;
    for (let i = 0; i < size; i++) {
      const v = get(i);
      if (v === runColor) {
        runLen++;
      } else {
        if (runLen >= 5) sub += 3 + (runLen - 5);
        runColor = v;
        runLen = 1;
      }
    }
    if (runLen >= 5) sub += 3 + (runLen - 5);
    return sub;
  };
  for (let r = 0; r < size; r++) penalty += runPenalty((c) => cells[r]![c]!);
  for (let c = 0; c < size; c++) penalty += runPenalty((r) => cells[r]![c]!);

  // Rule 2: 2×2 blocks of one colour.
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const v = cells[r]![c];
      if (
        v === cells[r]![c + 1] &&
        v === cells[r + 1]![c] &&
        v === cells[r + 1]![c + 1]
      ) {
        penalty += 3;
      }
    }
  }

  // Rule 3: 1:1:3:1:1 finder-like patterns bounded by four light modules.
  const P1 = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
  const P2 = [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1];
  const matches = (get: (i: number) => number, start: number, pat: number[]): boolean => {
    for (let k = 0; k < pat.length; k++) if (get(start + k) !== pat[k]!) return false;
    return true;
  };
  for (let r = 0; r < size; r++) {
    for (let c = 0; c <= size - 11; c++) {
      const get = (i: number): number => cells[r]![i]!;
      if (matches(get, c, P1) || matches(get, c, P2)) penalty += 40;
    }
  }
  for (let c = 0; c < size; c++) {
    for (let r = 0; r <= size - 11; r++) {
      const get = (i: number): number => cells[i]![c]!;
      if (matches(get, r, P1) || matches(get, r, P2)) penalty += 40;
    }
  }

  // Rule 4: deviation of dark-module proportion from 50%.
  let dark = 0;
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) dark += cells[r]![c]!;
  const percent = (dark * 100) / (size * size);
  penalty += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return penalty;
}

/**
 * Encode `text` as a QR symbol matrix (true = dark module). No quiet zone —
 * the renderer adds it. Uses byte mode, auto-selects the smallest fitting
 * version (1–10), and picks the mask with the lowest penalty.
 *
 * @throws if the text does not fit in version 10 at the chosen EC level.
 */
export function encodeQr(text: string, opts?: { ecLevel?: EcLevel }): boolean[][] {
  const ec: EcLevel = opts?.ecLevel ?? 'M';
  const data = new TextEncoder().encode(text);
  const version = selectVersion(data.length, ec);

  const dataCodewords = buildDataCodewords(data, version, ec);
  const finalCodewords = buildFinalCodewords(dataCodewords, version, ec);

  const grid = buildFunctionPatterns(version);
  placeData(grid, finalCodewords);

  let best: number[][] | null = null;
  let bestPenalty = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const trial = grid.cells.map((row) => row.slice());
    applyMask(trial, grid.reserved, mask);
    drawFormat(trial, grid.size, ec, mask);
    const p = penaltyScore(trial);
    if (p < bestPenalty) {
      bestPenalty = p;
      best = trial;
    }
  }

  // best is always set (loop runs at least once).
  return (best as number[][]).map((row) => row.map((v) => v === 1));
}
